import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { VerificationType } from '../../../globals';
import { getEnumValue } from '../../enum-helper';

@Component({
  selector: 'ez-type-enrollment',
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './type-enrollment.component.html',
  styleUrls: ['./type-enrollment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypeEnrollmentComponent {
  verificationTypeEnum = VerificationType;
  verificationType: VerificationType;
  isValid: boolean;
  protected readonly errorMessage = signal('');

  readonly #fb = inject(FormBuilder);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  form: FormGroup;

  get isAuthenticator(): boolean {
    return this.verificationType === VerificationType.AUTHENTICATOR;
  }

  get isSms(): boolean {
    return this.verificationType === VerificationType.SMS;
  }

  get displayName(): string {
    switch (this.verificationType) {
      case VerificationType.SMS: return 'text';
      case VerificationType.EMAIL: return 'email';
      case VerificationType.AUTHENTICATOR: return 'an authenticator';
      default: return '';
    }
  }

  constructor(elementRef: ElementRef) {
    const urlParams = new URLSearchParams(window.location.search);
    const verificationType = urlParams.get('verificationType') ?? 'SMS';

    this.verificationType = getEnumValue(verificationType, VerificationType) ?? VerificationType.SMS;

    const getAttribute = elementRef.nativeElement.getAttribute.bind(elementRef.nativeElement);
    this.isValid = getAttribute('is-valid') === 'is-valid';
    this.errorMessage.set(getAttribute('error-message') ?? '');

    switch (this.verificationType) {
      case VerificationType.SMS:
        this.form = this.#fb.group({
          phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
        });
        break;

      case VerificationType.EMAIL:
        this.form = this.#fb.group({
          email: ['', [Validators.required, Validators.email]],
        });
        break;

      default: this.form = this.#fb.group({});
    }
  }

  submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = new FormData();

    switch (this.verificationType) {
      case VerificationType.SMS:
        formData.append('OptionData.Display', this.form.value.phoneNumber);
        break;

      case VerificationType.EMAIL:
        formData.append('OptionData.Display', this.form.value.email);
        break;

      case VerificationType.AUTHENTICATOR:
        formData.append('OptionData.Display', '');
        break;
    }

    fetch('', { method: 'POST', body: formData }).then(async res => {
      if (res.redirected) {
        window.location.href = res.url;
      } else {
        const data = await res.json();
        this.isValid = false;
        this.errorMessage.set(data.errorMessage);
        this.#changeDetectorRef.markForCheck();
      }
    });
  }
}
